import type {
	IAdProvider,
	SizeMappingEntry,
} from '../types/ad-manager.interfaces';
import type {
	AdTargetingKey,
	IDefinedSlot,
	IGoogleTag,
	ILazyLoadParams,
	IPubAds,
	ISizeMapping,
} from '../types/google-tag.types';

const GAM_READY_EVENT = 'gam_ready';

const getGoogleTag = (): IGoogleTag | undefined => {
	if (typeof window === 'undefined') return undefined;
	return (window as unknown as { googletag?: IGoogleTag }).googletag;
};

const getPubAds = (): IPubAds | undefined => getGoogleTag()?.pubads?.();

export class GoogleAdProvider implements IAdProvider {
	isReady(): boolean {
		return getGoogleTag()?.pubadsReady === true;
	}

	waitUntilReady(signal?: AbortSignal): Promise<void> {
		if (this.isReady()) return Promise.resolve();

		return new Promise<void>((resolve, reject) => {
			let resolved = false;

			const finish = (): void => {
				if (resolved) return;
				resolved = true;
				clearInterval(interval);
				document.removeEventListener(GAM_READY_EVENT, onEvent);
				signal?.removeEventListener('abort', onAbort);
				resolve();
			};

			const onAbort = (): void => {
				if (resolved) return;
				resolved = true;
				clearInterval(interval);
				document.removeEventListener(GAM_READY_EVENT, onEvent);
				reject(new Error('AbortError'));
			};

			const onEvent = (): void => {
				if (this.isReady()) finish();
			};

			if (signal?.aborted) {
				reject(new Error('AbortError'));
				return;
			}

			signal?.addEventListener('abort', onAbort);
			document.addEventListener(GAM_READY_EVENT, onEvent);

			// Poll as a safety net (some portal setups fire gam_ready before we listen)
			const interval = setInterval(() => {
				if (this.isReady()) finish();
			}, 100);
		});
	}

	cmdPush(callback: () => void): void {
		const gt = getGoogleTag();
		gt?.cmd?.push(callback);
	}

	defineSlot(adUnitPath: string, size: [number, number], containerId: string): IDefinedSlot | undefined {
		const gt = getGoogleTag();
		if (!gt?.defineSlot) return undefined;

		const existingSlot = getPubAds()?.getSlots?.()?.find(
			(s) => s.getSlotElementId() === containerId,
		);
		if (existingSlot) return existingSlot;

		return gt.defineSlot(adUnitPath, size, containerId);
	}

	addServiceToSlot(slot: IDefinedSlot): IDefinedSlot | undefined {
		const pubads = getPubAds();
		if (!pubads || !slot.addService) return undefined;
		slot.addService(pubads);
		return slot;
	}

	addSizeMappingToSlot(slot: IDefinedSlot, mapping: ISizeMapping): void {
		slot.defineSizeMapping?.(mapping);
	}

	buildSizeMapping(entries: SizeMappingEntry[]): ISizeMapping | undefined {
		const gt = getGoogleTag();
		if (!gt?.sizeMapping) return undefined;

		let builder: ISizeMapping | undefined = gt.sizeMapping();
		for (const entry of entries) {
			builder = builder?.addSize?.(entry.viewportSize, entry.slotSize);
		}
		// Append the "hide at 0,0" catch-all
		builder = builder?.addSize?.([0, 0], []);
		return builder?.build?.();
	}

	enableSingleRequest(): void {
		getPubAds()?.enableSingleRequest?.();
	}

	collapseEmptyDivs(): void {
		getPubAds()?.collapseEmptyDivs?.();
	}

	disableInitialLoad(): void {
		getPubAds()?.disableInitialLoad?.();
	}

	enableLazyLoad(params: ILazyLoadParams): void {
		getPubAds()?.enableLazyLoad?.(params);
	}

	enableServices(): void {
		getGoogleTag()?.enableServices?.();
	}

	displayContainer(containerId: string): void {
		getGoogleTag()?.display?.(containerId);
	}

	setTargeting(key: AdTargetingKey, value: string | string[]): void {
		getPubAds()?.setTargeting?.(key, value);
	}

	getTargeting(key: AdTargetingKey): string[] {
		return getPubAds()?.getTargeting?.(key) ?? [];
	}

	clearTargeting(key: AdTargetingKey): void {
		getPubAds()?.clearTargeting?.(key);
	}

	refreshSlots(slots: IDefinedSlot[]): void {
		getPubAds()?.refresh?.(slots);
	}
}
